import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import dayjs, { type Dayjs } from 'dayjs';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  Typography,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { getRentCarLocations } from '../../../api/endpoints/rentCar';
import { ApiErrorAlert } from '../../../components/ApiErrorAlert';
import { PersonnelAutocomplete } from '../../../components/PersonnelAutocomplete';
import type { RentCarLocation, RentCarSearchRequest } from '../../../api/types/rentCar';
import type { CorporateEmployee } from '../../../api/types/corporateEmployee';

interface SearchFormStepProps {
  loading?: boolean;
  error?: unknown;
  onSubmit: (request: RentCarSearchRequest) => void;
}

export function SearchFormStep({ loading, error, onSubmit }: SearchFormStepProps) {
  const [pickupPointId, setPickupPointId] = useState('');
  const [dropOffPointId, setDropOffPointId] = useState('');
  const [pickupDatetime, setPickupDatetime] = useState<Dayjs | null>(dayjs());
  const [dropOffDatetime, setDropOffDatetime] = useState<Dayjs | null>(dayjs());
  const [age, setAge] = useState('30');
  const [employee, setEmployee] = useState<CorporateEmployee | null>(null);

  const locationsQuery = useQuery({
    queryKey: ['rentCarLocations'],
    queryFn: () => getRentCarLocations(),
    staleTime: 5 * 60 * 1000,
  });
  const locations = locationsQuery.data?.rentCarGetLocationsList ?? [];

  const isValid = !!pickupPointId && !!dropOffPointId && !!pickupDatetime && !!dropOffDatetime && !!age;

  const handleSearch = () => {
    if (!isValid || !pickupDatetime || !dropOffDatetime) return;
    onSubmit({
      pickupPointId,
      dropOffPointId,
      pickupDatetime: pickupDatetime.toISOString(),
      dropOffDatetime: dropOffDatetime.toISOString(),
      age: Number(age),
      employeeID: employee?.employeeID,
    });
  };

  const locationLabel = (loc: RentCarLocation) =>
    [loc.name ?? loc.code ?? String(loc.id ?? ''), loc.cityName].filter(Boolean).join(' — ');

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Araç Kiralama Arama
      </Typography>
      {locations.length === 0 && !locationsQuery.isLoading && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Lokasyon listesi bu hesap için boş döndü — alış/bırakış lokasyon ID'lerini doğrudan girebilirsiniz.
        </Alert>
      )}
      <ApiErrorAlert error={error} sx={{ mb: 2 }} />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Autocomplete
            options={locations}
            loading={locationsQuery.isLoading}
            getOptionLabel={locationLabel}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            onChange={(_e, value) => setPickupPointId(value ? String(value.id ?? '') : pickupPointId)}
            renderInput={(params) => <TextField {...params} label="Alış lokasyonu ara" size="small" />}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Autocomplete
            options={locations}
            loading={locationsQuery.isLoading}
            getOptionLabel={locationLabel}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            onChange={(_e, value) => setDropOffPointId(value ? String(value.id ?? '') : dropOffPointId)}
            renderInput={(params) => <TextField {...params} label="Bırakış lokasyonu ara" size="small" />}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Alış Lokasyon ID"
            size="small"
            fullWidth
            value={pickupPointId}
            onChange={(e) => setPickupPointId(e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Bırakış Lokasyon ID"
            size="small"
            fullWidth
            value={dropOffPointId}
            onChange={(e) => setDropOffPointId(e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <DateTimePicker
            label="Alış Tarihi/Saati"
            value={pickupDatetime}
            onChange={(value) => setPickupDatetime(value)}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <DateTimePicker
            label="Bırakış Tarihi/Saati"
            value={dropOffDatetime}
            onChange={(value) => setDropOffDatetime(value)}
            slotProps={{ textField: { size: 'small', fullWidth: true } }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <TextField
            label="Sürücü Yaşı"
            size="small"
            type="number"
            fullWidth
            value={age}
            onChange={(e) => setAge(e.target.value)}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <PersonnelAutocomplete value={employee} onChange={setEmployee} />
        </Grid>
      </Grid>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
        <Button variant="contained" disabled={!isValid || loading} onClick={handleSearch}>
          {loading ? 'Aranıyor…' : 'Araç Ara'}
        </Button>
      </Box>
    </Paper>
  );
}
